import { type OpenGraph } from 'next-seo/lib/types';
import { memo, useMemo } from 'react';

import { type Board } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { PublicBoardClipSEO } from '~/components/Layouts/PublicBoardLayout/PublicBoardClipSEO';
import { PublicPageSeoTitle } from '~/components/v2/components/shared/seo/PublicPageSeoTitle';
import { createShortUrlBoardRoute } from '~/constants/routes';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';
import { useActiveClipSeoData } from '~/swr-hooks/shortUrl/useActiveClipSeoData';
import { type ClipSeoData, getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';

export type PublicBoardPageSeoTitleProps = {
  board: Pick<Board, 'description' | 'id' | 'thumbnails' | 'title'>;
  initialClipData?: ClipSeoData | null;
};

export const PublicBoardPageSeoTitle = memo(({ board, initialClipData }: PublicBoardPageSeoTitleProps) => {
  const { shortId } = useShortIdContext();
  const clipId = useURLClipIdSelector();
  const boardId = useURLBoardIdSelector();

  const activeClip = useActiveClipSeoData({ clipId, shortId, initialClipData });

  const url = useMemo(() => {
    return `https://app.air.inc${createShortUrlBoardRoute(shortId, board.id)}`;
  }, [shortId, board.id]);

  const openGraph: OpenGraph = useMemo(() => {
    const thumbnail = board.thumbnails?.[0];

    return {
      url,
      images: thumbnail ? [{ url: thumbnail, alt: '' }] : undefined,
    };
  }, [board.thumbnails, url]);

  if (clipId && activeClip && boardId) {
    return <PublicBoardClipSEO clip={activeClip} boardId={boardId} />;
  }

  return (
    <PublicPageSeoTitle
      canonical={url}
      description={getShareSeoDescription(board.description)}
      openGraph={openGraph}
      title={board.title}
    />
  );
});

PublicBoardPageSeoTitle.displayName = 'PublicBoardPageSeoTitle';
